import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { CountryCodeEnum } from 'features/common/types';

/**
 * Branding por agencia para el preview de cotización del cliente.
 *
 * Las agencias Mevuelo (UY/PY/BO/CO) comparten el violeta de marca; Tienda
 * Naranja usa su naranja; el modo external (Itaú Volar) su azul sólido.
 * Agregar una agencia nueva = agregar una entrada acá, sin tocar componentes.
 */
/**
 * Verde de la seña, transversal a todas las agencias (semántico, no de marca):
 * naranja/accent = cuánto cuesta, verde = con cuánto asegurás tu lugar HOY.
 * La seña es el gancho de venta y necesita SU color, consistente en cards,
 * bloques "Reservá ahora con seña" y barras inferiores.
 */
export const SIGN_GREEN = '#2E7D32';
export const SIGN_GREEN_BG = 'rgba(46, 125, 50, 0.08)';
export const SIGN_GREEN_BORDER = 'rgba(46, 125, 50, 0.20)';

export interface AgencyBranding {
	key: 'mevuelo' | 'tienda-naranja' | 'external';
	/** Color sólido de marca: selección de cards, íconos, badges. */
	accent: string;
	/** Fondo de los headers de sección y pills (gradiente o color sólido). */
	headerGradient: string;
	/** Lavado de fondo de la página del preview. */
	pageWash: string;
	/** Saludo inicial del preview (bloque "¡Hola!"), editable por agencia. */
	introTitle: string;
	introText: string;
	/** Header de la card de hotel sin seleccionar ("Tocar para seleccionar"). */
	hotelCardHeaderBg: string;
	/** Header y borde de la card de hotel seleccionada. */
	hotelCardSelectedBg: string;
	/**
	 * Tipografía de la agencia para el preview del cliente. Si no se define, se
	 * usa la del theme (Montserrat). Se aplica con un ThemeProvider anidado, así
	 * las Typography la heredan de verdad.
	 */
	fontFamily?: string;
}

const MEVUELO: AgencyBranding = {
	key: 'mevuelo',
	accent: '#31194B',
	headerGradient: 'linear-gradient(135deg, #31194B 0%, #4a246c 58%, #ED6602 155%)',
	pageWash:
		'linear-gradient(180deg, rgba(49, 25, 75, 0.055) 0%, rgba(237, 102, 2, 0.035) 220px, #fbfafc 520px, #ffffff 100%)',
	hotelCardHeaderBg: 'rgba(49, 25, 75, 0.72)',
	hotelCardSelectedBg: '#31194B',
	introTitle: '¡Hola!',
	introText:
		'En Mevuelo estamos emocionados de presentarte una propuesta de viaje especialmente diseñada para ofrecerte una experiencia excepcional.',
};

const TIENDA_NARANJA: AgencyBranding = {
	key: 'tienda-naranja',
	accent: '#EE7000',
	headerGradient: 'linear-gradient(135deg, #b34e00 0%, #EE7000 58%, #ffb703 155%)',
	pageWash:
		'linear-gradient(180deg, rgba(238, 112, 0, 0.06) 0%, rgba(255, 183, 8, 0.045) 220px, #fbfafc 520px, #ffffff 100%)',
	// Azul institucional de Tienda Naranja para el header de las cards de hotel.
	hotelCardHeaderBg: 'rgba(2, 4, 107, 0.78)',
	hotelCardSelectedBg: '#02046B',
	introTitle: '¡Hola!',
	// Copy editable por comercial: es la voz de Tienda Naranja, no la de Mevuelo.
	introText:
		'En Tienda Naranja Viajes armamos esta propuesta a tu medida, con el respaldo de Itaú y un asesor que te acompaña en todo el viaje.',
	// fontFamily: pendiente de que comercial defina la tipografía TN.
};

const EXTERNAL: AgencyBranding = {
	key: 'external',
	accent: '#06036c',
	// El co-brand external no usa gradiente: color sólido.
	headerGradient: '#06036c',
	pageWash: 'linear-gradient(180deg, rgba(6, 3, 108, 0.05) 0%, #fbfafc 420px, #ffffff 100%)',
	hotelCardHeaderBg: 'rgba(6, 3, 108, 0.72)',
	hotelCardSelectedBg: '#06036c',
	introTitle: '¡Hola!',
	introText:
		'En Sevens Travel estamos emocionados de presentarte una propuesta de viaje especialmente diseñada para ofrecerte una experiencia excepcional.',
};

export const getAgencyBranding = (country?: string, isExternal?: boolean): AgencyBranding => {
	if (isExternal) return EXTERNAL;
	if (country === CountryCodeEnum.TN_ITAU_PY) return TIENDA_NARANJA;
	return MEVUELO;
};

/**
 * Cuadraditos de pago ("Cuotas disponibles" / "Efectivo o transferencia") que
 * acompañan a los totales, configurables POR AGENCIA (acá la granularidad es
 * por país, no por marca: PY y UY financian distinto aunque ambas sean
 * Mevuelo).
 *
 * `installmentsCount` habilita el monto calculado ("15 cuotas de USD 160") y
 * va SOLO en co-brands mono-banco (TN/Itaú, Itaú Volar) donde la agencia
 * garantiza el plan. En las agencias Mevuelo las cuotas dependen del banco
 * emisor (PY/BO), del sello y la aerolínea (UY) — un monto calculado sería
 * una promesa que no podemos cumplir para cualquier tarjeta, así que ahí va
 * texto.
 */
export interface PaymentTeaserConfig {
	installmentsTitle: string;
	/** Nº de cuotas garantizado. Si está, el teaser calcula el monto por cuota. */
	installmentsCount?: number;
	/** Línea descriptiva de cuotas (autónoma: no depende del monto calculado). */
	installmentsNote: string;
	/** Mostrar el cuadradito de efectivo/transferencia. */
	showCash: boolean;
	cashTitle?: string;
	cashNote?: string;
}

const PAYMENT_TEASER_DEFAULT: PaymentTeaserConfig = {
	installmentsTitle: 'Cuotas disponibles',
	installmentsNote: 'Según tarjeta y banco.',
	showCash: true,
	cashTitle: 'Efectivo o transferencia',
	cashNote: 'Consultá beneficio disponible.',
};

const PAYMENT_TEASER_BY_COUNTRY: Partial<Record<string, PaymentTeaserConfig>> = {
	[CountryCodeEnum.URUGUAY]: {
		...PAYMENT_TEASER_DEFAULT,
		installmentsNote: 'Según sello de la tarjeta y aerolínea elegida.',
	},
	[CountryCodeEnum.PARAGUAY]: {
		...PAYMENT_TEASER_DEFAULT,
		installmentsNote: 'Planes por banco: mirá el detalle en Formas de pago, más abajo.',
	},
	[CountryCodeEnum.TN_ITAU_PY]: {
		installmentsTitle: 'Hasta 15 cuotas sin intereses',
		installmentsCount: 15,
		installmentsNote: 'Con tus tarjetas Itaú.',
		// TN no ofrece beneficio por efectivo/transferencia: el cuadradito
		// confunde más de lo que suma.
		showCash: false,
	},
};

// Mismo dato que el banner de Itaú Volar en el preview externo.
const PAYMENT_TEASER_EXTERNAL: PaymentTeaserConfig = {
	installmentsTitle: 'Hasta 18 cuotas sin recargo',
	installmentsCount: 18,
	installmentsNote: 'Con tus tarjetas Itaú Volar.',
	showCash: false,
};

export const getPaymentTeaser = (country?: string, isExternal?: boolean): PaymentTeaserConfig => {
	if (isExternal) return PAYMENT_TEASER_EXTERNAL;
	return PAYMENT_TEASER_BY_COUNTRY[country ?? ''] ?? PAYMENT_TEASER_DEFAULT;
};

export const usePaymentTeaser = (isExternal?: boolean): PaymentTeaserConfig => {
	const { auth } = useSelector((state: RootState) => state);
	return getPaymentTeaser(auth.country, isExternal);
};

/**
 * El país llega al store en el preview del cliente (clientPreview despacha
 * setUpdateCountry con el countryCode del snapshot) y ya está seteado en la
 * sesión del asesor, así que no hay que pasarlo por props.
 */
export const useAgencyBranding = (isExternal?: boolean): AgencyBranding => {
	const { auth } = useSelector((state: RootState) => state);
	return getAgencyBranding(auth.country, isExternal);
};
